<template>
  <v-dialog v-model="dialog" persistent max-width="1100" content-class="log-modal">
    <div class="header-container">
      <div class="title-block">
        <h3>Logs</h3>
        <span class="trip-subtitle" v-if="tripDetails.id">Trip ID: {{ tripDetails.id }}</span>
        <span class="status-pill trip-pill" v-if="tripStatus">
          Trip Status: <strong>{{ tripStatus }}</strong>
        </span>
      </div>
      <i class="fas fa-times" @click="closeDialog"></i>
    </div>

    <div v-if="loading" class="empty-logs">
      <v-progress-circular :width="4" :size="40" color="#1076a6" indeterminate></v-progress-circular>
    </div>
    <div v-else-if="allLogs.length === 0" class="empty-logs">No logs available</div>

    <!-- Trip events and billing entries in one table, oldest first -->
    <div class="log-list" v-else>
      <div class="log-header">
        <span class="col-date">Date</span>
        <span class="col-status">Status</span>
        <span class="col-action">Action</span>
        <span class="col-remarks">Remarks</span>
        <span class="col-by">Performed By</span>
        <span class="col-file">File / Location</span>
      </div>
      <div class="log-list-block">
        <div class="list-content" v-for="(log, index) in allLogs" :key="index">
          <span class="content-block col-date" v-if="log.time">
            <span class="date-line">{{ formatDate(log.time, "DD MMM YYYY") }}</span>
            <span class="time-line">{{ formatDate(log.time, "hh:mm:ss A") }}</span>
          </span>
          <span class="content-block col-date" v-else>—</span>
          <span class="content-block col-status"><span class="status-chip" :style="{ background: log.chipColor }">{{ log.label }}</span></span>
          <span class="content-block col-action">{{ log.action || "—" }}</span>
          <span class="content-block col-remarks">{{ log.remark || "—" }}</span>
          <span class="content-block col-by">{{ log.by || "—" }}</span>
          <div class="content-block file-block col-file" v-if="log.files">
            <span class="image-view" @click="previewImage(log.files)" v-if="checkPdfImage(log.files, 'image')">View Image</span>
            <span class="image-view" @click="previewPdf(log.files)" v-if="checkPdfImage(log.files, 'pdf')">View PDF</span>
          </div>
          <span class="content-block col-file" v-else-if="log.location">
            <a :href="`https://maps.google.com/?q=${log.location.lat},${log.location.lng}`" target="_blank" class="image-view">View location</a>
          </span>
          <span v-else class="content-block col-file">—</span>
        </div>
      </div>
    </div>

    <v-card>
      <viewer ref="viewerRef" :images="selectedImages"></viewer>
    </v-card>

    <v-dialog v-model="pdfViewer" width="800px">
      <v-card>
        <div class="pdf-buttons">
          <v-btn v-for="(file, index) in pdfSrc" :key="index" small @click="currentPdf = file" color="#286a92"> PDF {{ index + 1 }} </v-btn>
        </div>
        <object width="100%" :data="currentPdf" style="height: 90vh; overflow: auto" v-if="pdfViewer"></object>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import moment from "moment";
import axios from "@/axios/axios-global";

// Trip log dates are stored as text, e.g. "14-10-2026 2:05:31 PM".
const LOG_DATE_FORMAT = "DD-MM-YYYY h:mm:ss A";

export default {
  name: "VerificationLog",
  props: {
    tripDetails: Object,
  },
  data() {
    return {
      dialog: true,
      loading: false,
      tripEvents: [],

      selectedImages: [],
      viewerApi: null,

      pdfSrc: [],
      pdfViewer: false,
      currentPdf: "",
    };
  },
  computed: {
    tripStatus() {
      const status = this.tripDetails.status;
      return status && status.name ? status.name : "";
    },
    billingLogs() {
      return this.tripDetails.corporateverificationLog || [];
    },
    // The trip's own history (same data as the Trip Details timeline) merged with the
    // billing verification entries, oldest first, so the whole story reads in one list.
    allLogs() {
      const trip = this.tripEvents.map((event) => {
        const status = this.tripEventStatus(event.status);
        return {
          time: event.date ? moment(event.date, LOG_DATE_FORMAT).valueOf() : null,
          label: status.label,
          chipColor: status.color,
          action: event.status,
          // Trip events carry no remark of their own - the action is the whole entry.
          remark: "",
          by: event.employee,
          location: event.location,
        };
      });
      const billing = this.billingLogs.map((entry) => ({
        time: entry.date ? moment(entry.date).valueOf() : null,
        label: this.showRemarksType(entry.type),
        chipColor: this.getColor(entry.type),
        action: this.billingAction(entry.type),
        remark: entry.remark,
        by: entry.name,
        files: entry.rectifiedFiles && entry.rectifiedFiles.length ? entry.rectifiedFiles : null,
      }));
      return [...trip, ...billing].sort((a, b) => (a.time || 0) - (b.time || 0));
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.viewerRef && this.$refs.viewerRef.$viewerApi) {
        this.viewerApi = this.$refs.viewerRef.$viewerApi;
      }
    });
    this.getTripTimeline();
  },
  methods: {
    closeDialog() {
      this.$emit("close");
    },
    // bigId is the numeric tripid - a billing row's `id` is the display ID.
    async getTripTimeline() {
      this.loading = true;
      try {
        const response = await axios.post("trip/timeline", { tripid: this.tripDetails.bigId });
        this.tripEvents = response.status === 200 && Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        // The billing entries still show without the trip's history.
        this.tripEvents = [];
      }
      this.loading = false;
    },
    /*
     * A trip log row is free text ("Trip Started with reading - Driver", "Driver Unassign - SP Portal",
     * ...), written by several services. The status is read from its keywords; the order matters,
     * since a cancel or no-show wins over anything else in the same text.
     */
    tripEventStatus(action) {
      const text = (action || "").toLowerCase();
      const rules = [
        [/no show/, "No Show", "#e53950"],
        [/cancel/, "Cancelled", "#e53950"],
        [/reject/, "Rejected", "#e53950"],
        [/\bend(ed)?\b/, "Ended", "#4b5a69"],
        [/drop/, "Drop", "#f0414f"],
        [/board/, "Boarded", "#0e9fb5"],
        [/start/, "Started", "#1e88e5"],
        [/arrive/, "Arrived", "#7b2fe0"],
        [/on the way/, "On The Way", "#ff8a1c"],
        [/unassign/, "Unassigned", "#e53950"],
        [/driver assign|request send to driver|find driver/, "Driver Assigned", "#6d3b2a"],
        [/marshal/, "Marshal", "#6d3b2a"],
        [/servicepartner assign|partner assign/, "SP Assigned", "#4b5a69"],
        [/accept|confirm/, "Accepted", "#1aab55"],
        [/approve/, "Approved", "#1aab55"],
        [/schedule|created|booked/, "Scheduled", "#1857f5"],
        [/otp/, "OTP Verified", "#1aab55"],
        [/change|update|toll|expense|insert/, "Updated", "#8a94a3"],
      ];
      const match = rules.find(([pattern]) => pattern.test(text));
      if (match) return { label: match[1], color: match[2] };
      return { label: "Update", color: "#8a94a3" };
    },
    billingAction(type) {
      switch (type) {
        case "dispute":
          return "Dispute Raised";
        case "response":
          return "Dispute Rectified";
        case "reject":
          return "Trip Rejected";
        case "Invoice Cancelled":
          return "Invoice Cancelled";
        case "Invoice Generated":
        case "invoice Generated":
          return "Invoice Generated";
        default:
          return "Trip Verified";
      }
    },
    getColor(type) {
      if (!type) return "";
      return ["Invoice Cancelled", "dispute", "reject"].includes(type) ? "#e53950" : type === "response" ? "#ff8a1c" : "#1aab55";
    },
    showRemarksType(type) {
      switch (type) {
        case "dispute":
          return "Dispute";
        case "response":
          return "Rectified";
        case "reject":
          return "Rejected";
        case "Invoice Cancelled":
          return "Invoice Cancelled";
        case "Invoice Generated":
        case "invoice Generated":
          return "Invoice Generated";
        default:
          return "Verified";
      }
    },
    formatDate(time, format) {
      return moment(time).format(format);
    },
    checkPdfImage(files, type) {
      return type === "pdf" ? files.some((doc) => this.isPdf(doc)) : files.some((doc) => !this.isPdf(doc));
    },
    previewPdf(files) {
      this.pdfSrc = files.filter((doc) => this.isPdf(doc));
      if (this.pdfSrc.length > 0) this.currentPdf = this.pdfSrc[0];
      this.pdfViewer = true;
    },
    previewImage(files) {
      this.selectedImages = files.filter((doc) => !this.isPdf(doc));
      if (this.viewerApi) {
        this.viewerApi({ images: this.selectedImages, options: { inline: false } });
      }
    },
    isPdf(url) {
      return url && url.toLowerCase().includes(".pdf");
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .log-modal {
  background: white;
  padding: 10px 14px 14px;
  border-radius: 8px;
  .header-container {
    padding: 2px 2px 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .fas {
      color: #e53935;
      font-size: 1.1rem;
      cursor: pointer;
    }
    .title-block {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
      h3 {
        font-size: 1.05rem;
        font-weight: 600;
        color: #1f2937;
      }
    }
    .trip-subtitle {
      font-size: 12px;
      color: #4b5563;
      padding-left: 10px;
      border-left: 1px solid #d1d5db;
    }
    .status-pill {
      font-size: 12px;
      border: 1px solid;
      border-radius: 20px;
      padding: 2px 12px;
      &.trip-pill {
        color: #1d4ed8;
        border-color: #3b6ef5;
      }
    }
  }
  .empty-logs {
    padding: 40px 0;
    text-align: center;
    color: grey;
    font-size: 13px;
  }
  .log-list {
    overflow-y: auto;
    max-height: calc(100vh - 260px) !important;
    border-radius: 8px;
    .log-header {
      display: flex;
      position: sticky;
      top: 0;
      background: #eef1f5;
      z-index: 1;
      span {
        text-align: left;
        padding: 8px 10px;
        font-size: 12px;
        color: #4b5563;
      }
    }
    .col-date {
      width: 11%;
    }
    .col-status {
      width: 13%;
    }
    .col-action {
      width: 22%;
    }
    .col-remarks {
      width: 23%;
    }
    .col-by {
      width: 18%;
    }
    .col-file {
      width: 13%;
    }
    .log-list-block {
      .list-content {
        display: flex;
        align-items: flex-start;
        border-bottom: 1px solid #e5e7eb;
        .content-block {
          text-align: left;
          padding: 7px 10px;
          font-size: 12px;
          color: #1f2937;
          word-wrap: break-word;

          .image-view {
            font-size: 12px;
            color: #1d4ed8;
            text-decoration: underline;
            cursor: pointer;
          }
        }
        .status-chip {
          display: inline-block;
          color: #fff;
          font-size: 11px;
          font-weight: 500;
          padding: 2px 12px;
          border-radius: 20px;
          white-space: nowrap;
        }
        .file-block {
          display: flex;
          flex-direction: column;
        }
        .date-line,
        .time-line {
          display: block;
        }
        .date-line {
          color: #1f2937;
          font-weight: 500;
        }
        .time-line {
          color: #6b7280;
          margin-top: 1px;
        }
      }
    }
  }
}

.pdf-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  .v-btn {
    color: white;
  }
}
</style>
